import { Button, Form, Modal } from 'antd';
import Input from 'antd/es/input/Input';
import { useContext } from 'react';

import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useUpdateBooking } from 'app/hooks/data/core/admin/bookings/useUpdateBooking';
import type { BookingDetails } from 'app/typings/core/admin/bookings/booking';
import type { UpdateBookingRequest } from 'app/typings/core/admin/bookings/bookingForm';
import { emailAsciiRule } from 'app/utils/rules';

import styles from './Modals.module.scss';

type Props = {
  bookingDetails: BookingDetails;
  isEditEmailModalVisible: boolean;
  setIsEditEmailModalVisible: (isVisible: boolean) => void;
};

export const EditEmail = ({
  isEditEmailModalVisible,
  setIsEditEmailModalVisible,
  bookingDetails,
}: Props) => {
  const [form] = Form.useForm();
  const { enterViewMode } = useContext(ViewEditModeContext);
  const emailValue = Form.useWatch('email', form);

  const { mutateAsync: saveBooking } = useUpdateBooking();

  const onClose = () => {
    setIsEditEmailModalVisible(false);
    enterViewMode();
  };

  const onSubmit = async (values: UpdateBookingRequest) => {
    await saveBooking({ code: bookingDetails.code, payload: values });
    onClose();
  };

  const formValues = { email: bookingDetails.customerData.email };
  const isSaveDisabled =
    !emailValue || emailValue === bookingDetails.customerData.email;

  return (
    <Modal
      width={572}
      onCancel={onClose}
      okText="Save"
      open={isEditEmailModalVisible}
      title="Edit email"
      centered
      className={styles.editEmailModal}
      wrapClassName={styles.editEmailModalWrapper}
      footer={[
        <Button
          type="primary"
          onClick={() => form.submit()}
          disabled={isSaveDisabled}
          key="save"
        >
          Save
        </Button>,
      ]}
    >
      <Form form={form} onFinish={onSubmit} initialValues={formValues}>
        <FormLineLayout
          label="Email"
          isRequired
          rightClassName={styles.emailField}
        >
          <Form.Item
            name="email"
            rules={[
              { type: 'email', message: 'Please enter a valid email address' },
              emailAsciiRule,
            ]}
            noStyle
          >
            <Input type="email" />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() =>
              form.getFieldError('email').length > 0 && (
                <div className={styles.errorMessage}>
                  {form.getFieldError('email')[0]}
                </div>
              )
            }
          </Form.Item>
        </FormLineLayout>
      </Form>
    </Modal>
  );
};
